import React from 'react';
import { Card, CardContent, Typography, useTheme } from '@mui/material';
import {
  ComposedChart, LineChart, BarChart,
  Line, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { getCategoryColor } from '../constants/categoryColors';

// Dollar-formatted Y-axis tick
const fmtY = (v) => {
  const a = Math.abs(v);
  if (a >= 1000000) return '$' + (v / 1000000).toFixed(1) + 'M';
  if (a >= 1000)    return '$' + (v / 1000).toFixed(0) + 'k';
  return '$' + v.toFixed(0);
};

const fmtCurrency = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

// ── Monthly Chart (combo only — bars for income+expenses, line for net) ──
export function MonthlyChart({ data }) {
  const theme = useTheme();

  return (
    <ResponsiveContainer width="100%" height={300}>
      <ComposedChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
        <YAxis tickFormatter={fmtY} tick={{ fontSize: 12 }} width={62} />
        <Tooltip formatter={fmtCurrency} />
        <Legend />
        <Bar dataKey="income"   name="Income"   fill={theme.palette.success.main}
             radius={[4,4,0,0]} opacity={0.85} />
        <Bar dataKey="expenses" name="Expenses" fill={theme.palette.error.main}
             radius={[4,4,0,0]} opacity={0.85} />
        <Line type="monotone" dataKey="net" name="Net Savings"
              stroke={theme.palette.primary.main} strokeWidth={2.5} dot={{ r: 3 }} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// ── Category Pie Chart (uses shared getCategoryColor) ──
export function CategoryChart({ data }) {
  const total = data.reduce((s, d) => s + (d.total || 0), 0);
  const threshold = total * 0.05;

  const significant = data.filter(d => d.total >= threshold);
  const otherSum    = data.filter(d => d.total < threshold)
                         .reduce((s, d) => s + (d.total || 0), 0);

  const pieData = [
    ...significant.map(d => ({ name: d.category, value: d.total })),
    ...(otherSum > 0 ? [{ name: 'Other', value: parseFloat(otherSum.toFixed(2)) }] : []),
  ];

  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={pieData}
          dataKey="value"
          cx="50%" cy="50%"
          innerRadius={55}
          outerRadius={100}
        >
          {pieData.map((entry, idx) => (
            <Cell
              key={idx}
              fill={getCategoryColor(entry.name, idx)}
              opacity={entry.name === 'Other' ? 0.5 : 1}
            />
          ))}
        </Pie>
        <Tooltip formatter={fmtCurrency} />
        <Legend
          wrapperStyle={{ fontSize: 12, lineHeight: '22px', paddingTop: 8 }}
          formatter={(v) => v}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

// ── Forecast Chart ────────────────────────────────────
export function ForecastChart({ data }) {
  const theme = useTheme();
  return (
    <ResponsiveContainer width="100%" height={350}>
      <ComposedChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={11} />
        <YAxis tickFormatter={fmtY} tick={{ fontSize: 11 }} width={66} />
        <Tooltip formatter={fmtCurrency} />
        <Legend />
        <Bar dataKey="monthly_net" name="Monthly Savings"
             fill={theme.palette.secondary.main} opacity={0.55} radius={[2,2,0,0]} />
        <Line type="monotone" dataKey="net_worth" name="Net Worth"
              stroke={theme.palette.primary.main} strokeWidth={2} dot={false} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// ── Stat Card ─────────────────────────────────────────
export function StatCard({ title, value, color, subtitle }) {
  return (
    <Card elevation={2}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        <Typography variant="h4" fontWeight={700} color={color || 'text.primary'}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
